/**
 * SharedGalleryPage — Read-only gallery for a shared view.
 * Route: /s/:shareToken/:viewId
 * Reuses GalleryGridMode and NetflixPlayer without selection or toolbar.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Spin, Empty, Result, Button } from "antd";
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { getShareByToken } from "@/services/shareService";
import { convertPhotoUrls } from "@/utils/storage";
import { GalleryGridMode } from "@/components/GalleryGridMode";
import { NetflixPlayer } from "@/features/player";
import { PhotoViewer } from "@/components/PhotoViewer";
import { MaterialIcon } from "@/components/MaterialIcon";
import type { ProjectShare, View, Photo } from "@/types";

const { Title } = Typography;

type ViewMode = "grid" | "preview";

// No-op selection for read-only mode
const EMPTY_SET = new Set<string>();
const noopToggle = (_id: string, _idx: number, _shift: boolean) => {};
const isNeverSelected = (_id: string) => false;

export default function SharedGalleryPage() {
  const { shareToken, viewId } = useParams<{
    shareToken: string;
    viewId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewMode: ViewMode = searchParams.get("mode") === "preview" ? "preview" : "grid";

  const [share, setShare] = useState<ProjectShare | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"not_found" | "disabled" | "excluded" | null>(null);
  const [viewerState, setViewerState] = useState<{
    visible: boolean;
    photoId: string;
  } | null>(null);

  const viewerCurrentIndex = viewerState
    ? photos.findIndex((p) => p.photoId === viewerState.photoId)
    : -1;

  const photosForPlayer = useMemo(() => [...photos].reverse(), [photos]);

  // Fetch share document
  useEffect(() => {
    if (!shareToken) return;

    getShareByToken(shareToken)
      .then((s) => {
        if (!s) {
          setError("not_found");
          setLoading(false);
          return;
        }
        if (!s.enabled) {
          setError("disabled");
          setLoading(false);
          return;
        }
        if (viewId && s.excludedViewIds.includes(viewId)) {
          setError("excluded");
          setLoading(false);
          return;
        }
        setShare(s);
      })
      .catch(() => {
        setError("not_found");
        setLoading(false);
      });
  }, [shareToken, viewId]);

  // Fetch view metadata
  useEffect(() => {
    if (!share || !viewId) return;

    const viewsQuery = query(
      collectionGroup(db, "views"),
      where("projectId", "==", share.projectId)
    );

    const unsub = onSnapshot(viewsQuery, (snapshot) => {
      const found = snapshot.docs.find((doc) => doc.id === viewId);
      if (found) {
        setView({ ...found.data(), viewId: found.id } as View);
      } else {
        setError("not_found");
        setLoading(false);
      }
    });

    return () => unsub();
  }, [share, viewId]);

  // Subscribe to photos (include userId to reuse existing composite index)
  useEffect(() => {
    if (!share || !viewId) return;

    const photosQuery = query(
      collection(db, "photos"),
      where("userId", "==", share.userId),
      where("viewId", "==", viewId),
      orderBy("capturedAt", "desc")
    );

    const unsub = onSnapshot(
      photosQuery,
      (snapshot) => {
        const photosList = snapshot.docs.map((doc) => {
          const data = { ...doc.data(), photoId: doc.id } as any;
          return convertPhotoUrls(data);
        });
        setPhotos(photosList);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to load photos:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [share, viewId]);

  const handlePhotoClick = useCallback(
    (index: number) => {
      const actualIndex =
        viewMode === "preview" ? photos.length - 1 - index : index;
      const photo = photos[actualIndex];
      if (photo) {
        setViewerState({ visible: true, photoId: photo.photoId });
      }
    },
    [viewMode, photos]
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error === "not_found" || error === "excluded") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="404"
          title="View not found"
          subTitle="This view does not exist or is not shared."
          extra={
            <Button onClick={() => navigate(`/s/${shareToken}`)}>
              Back to project
            </Button>
          }
        />
      </div>
    );
  }

  if (error === "disabled") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Result
          status="403"
          title="Sharing disabled"
          subTitle="The photographer has disabled sharing for this project."
        />
      </div>
    );
  }

  if (!share) return null;

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Button
          icon={<MaterialIcon name="arrow_back" />}
          onClick={() => navigate(`/s/${share.shareToken}`)}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <Title level={4} style={{ margin: 0, minWidth: 0 }} ellipsis>
            {view?.name || "View"}
            {photos.length > 0 && (
              <span style={{ marginLeft: 12, fontWeight: 400, opacity: 0.7 }}>
                <MaterialIcon
                  name="photo_library"
                  size={20}
                  style={{ verticalAlign: "middle", marginRight: 4 }}
                />
                {photos.length.toLocaleString()}
              </span>
            )}
          </Title>
        </div>
      </div>

      {/* Gallery */}
      <div
        style={{
          flex: 1,
          padding: viewMode === "grid" ? "16px 24px" : 0,
          boxSizing: "border-box",
        }}
      >
        {photos.length === 0 ? (
          <Empty
            description="No photos in this view yet"
            style={{ paddingTop: 64 }}
          />
        ) : (
          <>
            {viewMode === "grid" && (
              <GalleryGridMode
                photos={photos}
                loading={false}
                selectedIds={EMPTY_SET}
                onPhotoClick={handlePhotoClick}
                onSelectionToggle={noopToggle}
                isSelected={isNeverSelected}
                timezone={view?.androidStatus?.device?.timezone}
                readOnly
              />
            )}
            {viewMode === "preview" && (
              <NetflixPlayer
                photos={photosForPlayer}
                loading={false}
                selectedIds={EMPTY_SET}
                onPhotoClick={handlePhotoClick}
                onSelectionToggle={noopToggle}
                isSelected={isNeverSelected}
                timezone={view?.androidStatus?.device?.timezone}
              />
            )}
          </>
        )}
      </div>

      {/* PhotoViewer Lightbox (read-only: no download, no share) */}
      {viewerState && viewerCurrentIndex >= 0 && (
        <PhotoViewer
          photo={photos[viewerCurrentIndex]}
          photos={photos}
          currentIndex={viewerCurrentIndex}
          showFullMetadata={false}
          timezone={view?.androidStatus?.device?.timezone}
          onClose={() => setViewerState(null)}
          onNavigate={(newIndex) => {
            const newPhoto = photos[newIndex];
            if (newPhoto) {
              setViewerState({ visible: true, photoId: newPhoto.photoId });
            }
          }}
        />
      )}
    </div>
  );
}
